'use client';

import { Suspense, useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import LoadingSpinner from '@/components/LoadingSpinner';
import CountryCodeSelector from '@/components/CountryCodeSelector';
import { validatePhone, formatPhoneE164, DEFAULT_COUNTRY, getMaxPhoneLength } from '@/lib/phone';
import type { Country } from '@/lib/phone';
import { customerSegmentToSignupUserType } from '@/lib/auth/signup-user-type';
import type { CustomerSegment } from '@/types';
import { TermsMarketingConsentLabel } from '@/components/auth/TermsMarketingConsentLabel';
import { TERMS_ACCEPTANCE_REQUIRED_MESSAGE } from '@/lib/marketing/consent-copy';

const PROFESSION_OPTIONS: Array<{ value: CustomerSegment; label: string }> = [
  { value: 'independent_advocate', label: 'Advocate / lawyer (independent)' },
  { value: 'law_firm', label: 'Law firm / chambers' },
  { value: 'in_house_legal', label: 'In-house counsel / legal team' },
  { value: 'business_litigation', label: 'Founder / director / business owner' },
  { value: 'legal_ops_or_clerk', label: 'Legal operations / clerk / paralegal' },
  { value: 'individual_litigant', label: 'Individual litigant' },
  { value: 'student_researcher', label: 'Student or researcher' },
  { value: 'other', label: 'Other' },
];

export default function RequestAccessPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      }
    >
      <RequestAccessForm />
    </Suspense>
  );
}

export function RequestAccessForm() {
  const searchParams = useSearchParams();
  const isCaseDiscoveryRequest = searchParams.get('intent') === 'case-discovery';

  const [name, setName] = useState(searchParams.get('prefill-name') || '');
  const [email, setEmail] = useState(searchParams.get('prefill-email') || '');
  const [profession, setProfession] = useState<CustomerSegment | ''>('');
  const [requirement, setRequirement] = useState('');

  const [selectedCountry, setSelectedCountry] = useState<Country>(DEFAULT_COUNTRY);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [verifiedPhoneE164, setVerifiedPhoneE164] = useState<string | null>(null);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const termsAcceptedRef = useRef(false);

  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const maxLen = getMaxPhoneLength(selectedCountry);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleSendOtp = async () => {
    setError('');
    if (!termsAcceptedRef.current) {
      setError(TERMS_ACCEPTANCE_REQUIRED_MESSAGE);
      return;
    }
    const validation = validatePhone(phone, selectedCountry.code);
    if (!validation.isValid) {
      setError(validation.error || 'Invalid phone number');
      return;
    }
    const formatted = formatPhoneE164(phone, selectedCountry.dialCode);
    if (!formatted) {
      setError('Invalid phone number');
      return;
    }

    setOtpSending(true);
    try {
      const res = await fetch('/api/auth/send-whatsapp-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: formatted, terms_accepted: termsAcceptedRef.current }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.error || 'Could not send OTP. Please try again.');
        return;
      }
      setOtpSent(true);
      setResendCooldown(60);
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError('');
    if (!termsAcceptedRef.current) {
      setError(TERMS_ACCEPTANCE_REQUIRED_MESSAGE);
      return;
    }
    if (!/^\d{6}$/.test(otp)) {
      setError('Enter the 6-digit code.');
      return;
    }
    const formatted = formatPhoneE164(phone, selectedCountry.dialCode);
    if (!formatted) {
      setError('Invalid phone number.');
      return;
    }

    setOtpVerifying(true);
    try {
      const res = await fetch('/api/auth/verify-phone-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: formatted, otp, terms_accepted: termsAcceptedRef.current }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.error || 'Invalid OTP.');
        return;
      }
      setPhoneVerified(true);
      setVerifiedPhoneE164(formatted);
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleEditPhone = () => {
    setPhoneVerified(false);
    setVerifiedPhoneE164(null);
    setOtpSent(false);
    setOtp('');
    setResendCooldown(0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!phoneVerified || !verifiedPhoneE164) {
      setError('Please verify your phone number first.');
      return;
    }
    if (!termsAcceptedRef.current) {
      setError(TERMS_ACCEPTANCE_REQUIRED_MESSAGE);
      return;
    }
    if (!profession) {
      setError('Please choose your profession.');
      return;
    }
    if (requirement.trim().length < 20) {
      setError('Please describe your requirement in at least 20 characters.');
      return;
    }

    const trimmedEmail = email.trim().toLowerCase();
    const professionLabel =
      PROFESSION_OPTIONS.find((option) => option.value === profession)?.label || profession;

    setSubmitting(true);
    try {
      const res = await fetch('/api/access-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: trimmedEmail,
          phone: verifiedPhoneE164,
          profession: professionLabel,
          customer_segment: profession,
          user_type: customerSegmentToSignupUserType(profession),
          requirement: requirement.trim(),
          intent: searchParams.get('intent') || undefined,
          source_surface: searchParams.get('source_surface') || undefined,
          lp_slug: searchParams.get('lp_slug') || undefined,
          utm_source: searchParams.get('utm_source') || undefined,
          utm_medium: searchParams.get('utm_medium') || undefined,
          utm_campaign: searchParams.get('utm_campaign') || undefined,
          terms_accepted: termsAcceptedRef.current,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.error || 'Could not submit request. Please try again.');
        setSubmitting(false);
        return;
      }
      setDone(true);
    } catch {
      setError('Network error. Please try again.');
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-paper-sunken flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10 space-y-4">
            <div className="mx-auto w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center">
              <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="eyebrow">Thank you</div>
            <h1 className="font-serif font-normal text-3xl text-gray-900 tracking-[-0.02em]">Request received</h1>
            <p className="text-gray-600 text-sm">
              {isCaseDiscoveryRequest
                ? 'Thanks — our team will review the names and court coverage you need, then get in touch, typically within one business day.'
                : 'Thanks — our team will review your request and get in touch, typically within one business day.'}
            </p>
            <div className="pt-4">
              <Link href="/" className="text-sm text-primary hover:underline">
                Back to homepage
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper-sunken py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-10">
          <div className="eyebrow mb-3">
            {isCaseDiscoveryRequest ? 'Case Discovery' : 'Custom onboarding'}
          </div>
          <h1 className="font-serif font-normal text-4xl md:text-5xl text-gray-900 tracking-[-0.02em] leading-[1.05]">
            {isCaseDiscoveryRequest ? 'Request Case Discovery' : 'Request Access'}
          </h1>
          <hr className="hair mx-auto mt-5 w-12" />
          <p className="mt-5 text-gray-600 max-w-md mx-auto">
            {isCaseDiscoveryRequest
              ? 'Tell us which people or company names you want monitored and the courts or tribunals most likely to matter.'
              : 'For firms, companies, high-volume teams, and custom onboarding. Self-serve signup remains open for everyone else.'}
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8 space-y-5"
        >
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              disabled={submitting}
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@firm.com"
              className="block w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              disabled={submitting}
            />
          </div>

          <div>
            <label htmlFor="profession" className="block text-sm font-medium text-gray-700 mb-1">
              Profession <span className="text-red-500">*</span>
            </label>
            <select
              id="profession"
              required
              value={profession}
              onChange={(e) => {
                setProfession(e.target.value as CustomerSegment);
                setError('');
              }}
              disabled={submitting}
              className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="">Select profession...</option>
              {PROFESSION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="requirement" className="block text-sm font-medium text-gray-700 mb-1">
              Requirement <span className="text-red-500">*</span>
            </label>
            <textarea
              id="requirement"
              required
              minLength={20}
              value={requirement}
              onChange={(e) => setRequirement(e.target.value)}
              rows={4}
              placeholder={
                isCaseDiscoveryRequest
                  ? 'Tell us the people or company names to monitor, whether cases filed against them, by them, or both matter, and the likely courts or tribunals.'
                  : 'Briefly tell us what you need help with.'
              }
              className="block w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              disabled={submitting}
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              WhatsApp mobile number <span className="text-red-500">*</span>
            </label>
            <p className="mb-2 text-xs text-gray-500">
              We verify this number with a one-time code before accepting the request.
            </p>
            {phoneVerified ? (
              <div className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 px-3 py-2.5">
                <div className="flex items-center gap-2 text-sm text-green-800">
                  <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>WhatsApp number verified</span>
                </div>
                <button type="button" onClick={handleEditPhone} className="text-xs text-primary hover:underline">
                  Edit
                </button>
              </div>
            ) : (
              <>
                <div className="flex gap-2">
                  <CountryCodeSelector
                    selectedCountry={selectedCountry}
                    onChange={setSelectedCountry}
                    disabled={otpSent}
                  />
                  <input
                    id="phone"
                    type="tel"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, maxLen))}
                    placeholder="98765 43210"
                    className="flex-1 block w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    disabled={otpSent}
                  />
                </div>
                <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm leading-5 text-gray-700">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(event) => {
                      termsAcceptedRef.current = event.target.checked;
                      setTermsAccepted(event.target.checked);
                    }}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    required
                  />
                  <TermsMarketingConsentLabel />
                </label>
                {!otpSent ? (
                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={otpSending || !phone || !termsAccepted}
                    className="mt-3 inline-flex items-center justify-center py-2 px-4 rounded-lg text-sm font-medium text-white bg-primary hover:bg-primary-700 disabled:opacity-50"
                  >
                    {otpSending ? <LoadingSpinner size="sm" className="text-white" /> : 'Send WhatsApp OTP'}
                  </button>
                ) : (
                  <div className="mt-3 space-y-2">
                    <label htmlFor="otp" className="block text-xs text-gray-600">
                      Enter the 6-digit code sent on WhatsApp
                    </label>
                    <div className="flex gap-2">
                      <input
                        id="otp"
                        type="text"
                        inputMode="numeric"
                        required
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        className="flex-1 block w-full rounded-lg border border-gray-300 px-3 py-2.5 tracking-[0.3em] focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                      />
                      <button
                        type="button"
                        onClick={handleVerifyOtp}
                        disabled={otpVerifying || otp.length !== 6 || !termsAccepted}
                        className="inline-flex items-center justify-center py-2 px-4 rounded-lg text-sm font-medium text-white bg-primary hover:bg-primary-700 disabled:opacity-50"
                      >
                        {otpVerifying ? <LoadingSpinner size="sm" className="text-white" /> : 'Verify'}
                      </button>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <button type="button" onClick={handleEditPhone} className="text-gray-600 hover:text-primary">
                        Change number
                      </button>
                      <button
                        type="button"
                        onClick={handleSendOtp}
                        disabled={resendCooldown > 0 || otpSending}
                        className="text-primary hover:underline disabled:opacity-50 disabled:no-underline"
                      >
                        {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={
              submitting
              || !phoneVerified
              || !profession
              || requirement.trim().length < 20
              || !termsAccepted
            }
            className="w-full flex items-center justify-center py-3 px-4 rounded-lg text-sm font-medium text-white bg-primary hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting
              ? <LoadingSpinner size="sm" className="text-white" />
              : isCaseDiscoveryRequest
                ? 'Request Case Discovery'
                : 'Submit request'}
          </button>

          <p className="text-center text-xs text-gray-500 pt-2">
            Already have access?{' '}
            <Link href="/signin" className="text-primary hover:underline">
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
